import posthog from 'posthog-js';
import { GlobalSearchEntityType } from 'types/globalSearch';
import { isPostHogEnabled } from './posthog';

/**
 * Global search's event taxonomy. Every search surface captures through these functions rather than
 * calling `posthog.capture` with a name of its own, so the four questions the feature exists to
 * answer — is it used, does it find things, does it replace the list pages, is the peek worth it —
 * stay answerable from one set of events.
 *
 * **The query text is never sent.** It carries client names and case numbers, so what goes out is
 * its length and what came back. There is deliberately no parameter here that could carry it.
 */

/** Which search surface an event came from. */
export type SearchSurface = 'dropdown' | 'results-page' | 'sheet';

function capture(event: string, properties: Record<string, unknown>): void {
    if (!isPostHogEnabled) {
        return;
    }
    posthog.capture(event, properties);
}

export interface SearchQueryRanEvent {
    surface: SearchSurface;
    /** Characters typed, not what they were. */
    queryLength: number;
    resultCount: number;
    /** Round trip for the request that produced the count, in milliseconds. */
    latencyMs: number;
}

export function captureSearchQueryRan({ surface, queryLength, resultCount, latencyMs }: SearchQueryRanEvent): void {
    capture('global_search_query_ran', {
        surface,
        query_length: queryLength,
        result_count: resultCount,
        zero_results: resultCount === 0,
        latency_ms: latencyMs,
    });
}

export interface SearchResultClickedEvent {
    surface: SearchSurface;
    entityType: GlobalSearchEntityType;
    /** Zero-based position in the list as drawn, which is what tells a good ranking from a lucky one. */
    position: number;
    bestBet: boolean;
}

export function captureSearchResultClicked({ surface, entityType, position, bestBet }: SearchResultClickedEvent): void {
    capture('global_search_result_clicked', {
        surface,
        entity_type: entityType,
        position,
        best_bet: bestBet,
    });
}

/**
 * The user left the panel for the full results page — the panel did not answer it alone.
 *
 * The surface is passed rather than assumed: the narrow sheet reaches the results page through the
 * same key handler as the header's box, and reporting both as `dropdown` would leave the `sheet`
 * surface absent from the one event that says the panel was not enough.
 */
export function captureSearchResultsPageOpened(surface: SearchSurface = 'dropdown'): void {
    capture('global_search_results_page_opened', { surface });
}

/** `link` is a record link anywhere else in the app, which raises the same card and nothing else of search's. */
export function captureSearchPeekOpened({
    surface,
    entityType,
}: {
    surface: SearchSurface | 'link';
    entityType: GlobalSearchEntityType;
}): void {
    capture('global_search_peek_opened', { surface, entity_type: entityType });
}
